import BaseTool from "./base/BaseTool";
import { getEnabledElement } from "component/toolManager/enabledElements.js";
import Math from "core/math";
import renderActiveReferenceLine from "./referencelines/renderActiveReferenceLine";
// Drawing
import { getNewContext } from "./../../draw/index.js";

/**
 * Internal API function to draw an image to a given enabled element
 *
 * @param {EnabledElement} enabledElement  Enabled Element to redraw
 * @param {Boolean} [invalidated = false] - true if pixel data has been invalidated and cached rendering should not be used
 * @returns {void}
 * @memberof Internal
 */
function drawImage(enabledElement, invalidated = false) {
  enabledElement.needsRedraw = true;
  if (invalidated) {
    enabledElement.invalid = true;
  }
}

/**
 * Forces the image to be updated/redrawn for the specified enabled element
 * @param {HTMLElement} element An HTML Element enabled for
 * @param {Boolean} [invalidated=false] Whether or not the image pixel data has been changed, necessitating a redraw
 *
 * @returns {void}
 * @memberof Drawing
 */
function updateImage(element, invalidated = false) {
  const enabledElement = element;

  //if (enabledElement.image === undefined && !enabledElement.layers.length) {
  if (enabledElement.image === undefined) {
    //throw new Error("updateImage: image has not been loaded yet");
    return;
  }

  drawImage(enabledElement, invalidated);
}

/**
 * When enabled, this tool will display references lines for each source
 * enabledElement in the provided synchronizer. This tool can also be configured
 * to use a custom renderer for alternative reference line rendering behavior
 *
 * TODO: Need to watch for configuration changes to update ToolState
 * TODO:
 *
 * @export @public @class
 * @name ReferenceLinesTool
 * @classdesc Tool for displaying reference lines of other enabledElements
 * @extends Tools.Base.BaseTool
 */
export default class ReferenceLinesTool extends BaseTool {
  constructor(props = {}) {
    const defaultProps = {
      name: "ReferenceLines",
      mixins: ["enabledOrDisabledBinaryTool"],
      configuration: {
        renderer: renderActiveReferenceLine,
      },
    };
    //super(props, defaultProps);
    const initialConfiguration = Object.assign(defaultProps, props);

    super(initialConfiguration);

    this.initialConfiguration = initialConfiguration;
    this.renderer = null;
    this.synchronizationContext = null;
  }

  async enabledCallback(element, { synchronizationContext } = {}) {
    const renderer = this.configuration.renderer;
    const enabledElement = getEnabledElement(element);

    if (!enabledElement || !renderer || !synchronizationContext) {
      // TODO: Unable to add tool state, image never loaded.
      // Should we `setToolDisabledForElement` here?
      // logger.warn(
      //   `Unable to enable ${this.name}. Exiting enable callback. Tool will be enabled, but will not render.`
      // );

      return;
    }
    this.renderer = renderer;
    this.synchronizationContext = synchronizationContext;

    this.forceImageUpdate(element);
  }

  disabledCallback(element) {
    /**
     * 도구 기능 비활성화
     */
    this.renderer = null;
    this.synchronizationContext = null;
    this.forceImageUpdate(element);
  }

  forceImageUpdate(element) {
    const enabledElement = getEnabledElement(element);

    if (enabledElement.image) {
      updateImage(element);
    }
  }

  renderToolData(evt) {
    const eventData = evt.detail;

    /**
     * 1. 레퍼런스 라인을 그리는 렌더러 또는 동기화 Context가 초기화 되지 않았다면,
     * 레퍼런스 라인 그리기를 중단한다.
     */
    if (!this.renderer || !this.synchronizationContext) {
      return;
    }

    const sourceElements = this.synchronizationContext.getSourceElements();

    /**
     * 2. Active 상태인 DCMView를 찾고, 그 DCMView를 referenceElement로 지정한다.
     */
    let referenceElement = null;
    for (let sourceElement of sourceElements) {
      const classnameList = sourceElement.classList;

      let foundActiveViewIdx = -1;
      for (let i = 0; i < classnameList.length; i++) {
        const item = classnameList[i];
        if (typeof item !== "string" || item !== "active") {
          continue;
        }
        foundActiveViewIdx = i;
        break;
      }

      if (foundActiveViewIdx === -1) {
        continue;
      }

      referenceElement = sourceElement;
    }

    /// 만약, active 상태인 Element를 찾지 못했으면 더이상 진행하지 않는다.
    if (referenceElement === null) {
      return;
    }

    /**
     * 3. Active 상태인 DCMView와 연결된 DCMView 들을 찾는다.
     */
    const targetElements = this.synchronizationContext.getTargetElements(
      referenceElement
    );

    /**
     * 4. Active 상태인 DCMView와 연결된 DCMView 들을 찾는다.
     */
    targetElements
      .filter((targetElement, index) => {
        let targetElementStub = getEnabledElement(targetElement);
        if (!targetElementStub.viewport) {
          return false;
        }
        return true;
      })
      .map((targetElement, index) => {
        let targetElementStub = getEnabledElement(targetElement);
        const context = getNewContext(targetElementStub.canvas);
        Math.coordinate.setToPixelCoordinateSystem(targetElementStub, context);

        // Render it
        this.renderer(context, eventData, targetElement, referenceElement);
      });
  }
}
